import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { BUSINESS_MODEL, IMAGES } from "@/data/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const VALUES = [
  { title: "Trusted Brands", text: "We only work with brands we can stand behind — across paints, plumbing, bathware and construction solutions." },
  { title: "Product Variety", text: "Multiple categories under one roof, so customers don't have to run between shops to complete a project." },
  { title: "Multi-Category Expertise", text: "Real product knowledge built over years of serving homeowners and professionals alike." },
  { title: "Customer Relationships", text: "Many of our customers have been with us for years — and send their friends, family and sites to us." },
  { title: "Professional Service", text: "Straight answers, fair dealing and guidance that puts your requirement first." },
  { title: "Long-Term Approach", text: "We build for relationships, not transactions. That's how a family business earns its name." },
];

export default function About() {
  usePageMeta(
    "About Us | Chhannulal & Brothers (CLB)",
    "Chhannulal & Brothers is a family-run business across distribution, wholesale and retail — trusted brands and complete home solutions for construction, renovation and interiors."
  );
  return (
    <main>
      <section className="bg-darknavy pb-24 pt-36 text-cream lg:pb-32 lg:pt-44" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline dark>About CLB</Overline>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-light tracking-tight sm:text-6xl">
              Building Trust. <span className="font-medium text-gold">Supplying Solutions.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-cream/75 sm:text-lg">
              Chhannulal &amp; Brothers is a family-run business operating across distribution, wholesale and retail — serving customers with products for construction, renovation, interiors and home improvement. Part of the wider Chhannulal Group family of businesses, CLB is where trusted brands and complete home solutions come together under one roof.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="relative">
                  <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden="true" />
                  <div className="relative h-80 overflow-hidden sm:h-[26rem]">
                    <SafeImage src={IMAGES.about} alt="A modern home built with materials supplied by Chhannulal & Brothers" className="h-full w-full object-cover" />
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <Overline>Our story</Overline>
                <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-4xl">
                  A family business, run <span className="font-medium">professionally.</span>
                </h2>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-darknavy/70">
                  <p>
                    What began as a family business has grown into a multi-category destination for building materials and home solutions. The name Chhannulal &amp; Brothers carries the trust of the family behind it — and CLB is how a new generation of customers is coming to know us.
                  </p>
                  <p>
                    We serve homeowners building their first home, contractors on tight schedules, builders with project-scale requirements, and professionals — architects, plumbers, painters — who need a supplier that understands their work.
                  </p>
                  <p>
                    Our promise is simple: trusted brands, honest guidance, and service that brings you back.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="about-model">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline>Three ways we serve you</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-4xl">Our Business Model</h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-darknavy/10 bg-darknavy/10 md:grid-cols-3">
            {BUSINESS_MODEL.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1} className="bg-white">
                <div className="h-full p-8 transition-colors duration-500 hover:bg-cream lg:p-10">
                  <span className="font-display text-4xl font-light text-gold">{m.num}</span>
                  <h3 className="mt-5 font-display text-2xl font-medium text-darknavy">{m.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-darknavy/60">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline>What we stand for</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-4xl">How CLB Works</h2>
          </Reveal>
          <div className="mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.04}>
                <div className="group flex flex-col gap-3 border-t border-darknavy/10 py-7 transition-colors duration-500 hover:bg-white sm:flex-row sm:items-baseline sm:gap-12 sm:px-4">
                  <span className="font-display text-2xl font-light text-gold sm:w-16">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl font-medium text-darknavy sm:w-72">{v.title}</h3>
                  <p className="max-w-2xl text-sm leading-relaxed text-darknavy/60 sm:text-base">{v.text}</p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-darknavy/10" />
          </div>
          <Reveal delay={0.2}>
            <Link to="/contact" data-testid="about-contact-cta" className="group mt-12 inline-flex items-center gap-3 bg-navy px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-aqua">
              Get in Touch
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
